import { type OrderDTO, OrderStatus } from 'ecosystem'

/**
 * An order is provisioned exactly once. Pre-provision it sits in DRAFT or CREATED and flips to PAID
 * during provisioning. Anything other than DRAFT/CREATED means it was already provisioned (or
 * later transitioned by OM, e.g. DELIVERED/CANCELLED), so a second provision attempt (e.g.
 * confirmation page + payment push racing, or a payment-provider push retry) must be skipped to
 * avoid double shipment booking, duplicate writes, or downgrading a post-PAID status back to PAID.
 */
export const isOrderAlreadyProvisioned = (order: OrderDTO): boolean =>
  order.status !== OrderStatus.CREATED && order.status !== OrderStatus.DRAFT

/** How long an order is considered "fresh" — i.e. the confirmation page is the responsible
 *  provisioner and the payment push should stand down. */
export const FRESH_ORDER_WINDOW_MS = 30 * 60 * 1000

/**
 * True if the order was created/updated within the freshness window. The payment push provisions
 * only as a fallback: while an order is still fresh, the confirmation page owns provisioning, so
 * the push must abstain — this keeps the page the single writer during the post-checkout window and
 * removes the page-vs-push race entirely for fresh orders. Once an order is older than the window
 * and still unprovisioned, the page clearly never ran (user abandoned), so the push provisions it.
 */
export const isOrderFresh = (order: OrderDTO): boolean => {
  const timestamp = order.updated ?? order.created

  if (timestamp === undefined || timestamp === null) {
    // No timestamp to reason about — don't block the fallback, the status guard still prevents
    // double-writes of an already-provisioned order.
    return false
  }

  // BE currently returns Unix seconds (with ms as the decimal fraction, e.g. 1780905798.334).
  // Fall back to Date parsing so ISO strings (e.g. "2026-06-08T08:03:18.334Z") also work if BE
  // ever switches format.
  const numeric = typeof timestamp === 'number' ? timestamp : Number(timestamp)
  const lastTouched = Number.isFinite(numeric)
    ? numeric * 1000
    : new Date(timestamp as string).getTime()

  if (Number.isNaN(lastTouched)) {
    return false
  }

  return Date.now() - lastTouched < FRESH_ORDER_WINDOW_MS
}
